import {useRef, useEffect} from 'react';
import {FieldValues, useWatch} from 'react-hook-form';

import {equals} from 'ramda';

import {useDebouncedCallback} from 'shared/web';

import {useFormContext} from '../../components/Form/hooks/useFormContext';

type DebounceConfig = {
  delay: number;
  wait: number;
};

type AutoSaveProps<TFieldValues extends FieldValues = FieldValues> = {
  onSave: (values: TFieldValues) => void;
  debounceConfig?: DebounceConfig;
};

/**
 * AutoSave component for handling form auto-saving. This AutoSave should only be used as a one-way auto-save.
 * Only for saving data. Error handling logic is always implemented on the layer above this component.
 *
 * @template TFieldValues - Generic form type.
 * @param {AutoSaveProps<TFieldValues>} props - Simple input and output, `onSave` callback which is called after every change. DebounceConfig to configure the debounce that is used in the AutoSave component.
 * @returns {null} - Returns null as the AutoSave component doesn't render anything directly.
 */
export function AutoSave<TFieldValues extends FieldValues = FieldValues>(
  props: AutoSaveProps<TFieldValues>
): null {
  const methods = useFormContext<TFieldValues>();
  const isDirty = methods.formState.isDirty;
  const dirtyFields = methods.formState.dirtyFields;

  const watchedData = useWatch({
    control: methods.control,
  });

  const savedRef = useRef(false);
  const initialRenderRef = useRef(true);
  const previousDataRef = useRef<TFieldValues | null>(null);

  const debouncedSave = useDebouncedCallback(
    (data: TFieldValues) => {
      props.onSave(data);
    },
    props.debounceConfig?.delay ?? DEFAULT_DEBOUNCED_DELAY,
    props.debounceConfig?.wait ?? DEFAULT_DEBOUNCED_WAIT
  );

  useEffect(
    function autoSaveDirtyChanges() {
      if (initialRenderRef.current) {
        initialRenderRef.current = false;
        previousDataRef.current = watchedData as TFieldValues;
        return;
      }

      // Only save if the form is dirty and there are dirty fields
      if (isDirty && Object.keys(dirtyFields).length > 0) {
        const currentData = watchedData as TFieldValues;

        // Deep comparison to ensure data has actually changed
        if (!previousDataRef.current || !equals(previousDataRef.current, currentData)) {
          debouncedSave(currentData);
          savedRef.current = true;
          previousDataRef.current = currentData;
        }
      }

      if (!isDirty) {
        savedRef.current = false;
      }
    },
    [isDirty, dirtyFields, watchedData, debouncedSave]
  );

  return null;
}

const DEFAULT_DEBOUNCED_DELAY = 700;
const DEFAULT_DEBOUNCED_WAIT = 60000;
